import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ErrorStateVariant = "inline" | "page";

export interface ErrorStateProps {
  /** Human-readable error message (use `errorCopy(error)` to derive). */
  message: string;
  /** Retry handler. When provided, a retry button is rendered. */
  retry?: () => void;
  /** Layout treatment. Default: "page". */
  variant?: ErrorStateVariant;
  /** Optional className for layout composition. */
  className?: string;
}

/**
 * Error-state primitive. Renders a `role="alert"` region.
 *
 * Spec: docs/architecture/states-pattern.md §2.3.
 */
export function ErrorState({
  message,
  retry,
  variant = "page",
  className,
}: ErrorStateProps): ReactNode {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (retry && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [retry]);

  return (
    <section
      role="alert"
      aria-live="assertive"
      data-state="error"
      data-variant={variant}
      className={cn(
        "flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-destructive/30 bg-destructive/5 text-center",
        variant === "page" ? "min-h-[320px] p-8" : "p-4",
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/15 text-destructive"
      >
        <WarningCircle className="h-6 w-6" weight="bold" />
      </div>
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">Algo deu errado</h2>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">{message}</p>
      </div>
      {retry ? (
        <Button ref={buttonRef} type="button" onClick={retry}>
          Tentar novamente
        </Button>
      ) : null}
    </section>
  );
}
